import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useEffect, useState } from "react";
import type { GasReading } from "@/api/queries/incidents";

/*
 * Series encoding: one fixed hue per gas. Colour follows the gas, never its
 * position, so a reading set without H2S doesn't repaint VOC. The hues are the
 * first four slots of the data-viz categorical palette, stepped per theme and
 * checked with its validator against this app's card surfaces (#FFFFFF light,
 * #111820 dark): every pair that sits next to each other in the legend clears
 * the colour-vision-deficiency and normal-vision floors, and every line clears
 * 3:1 contrast. Red is left out on purpose: next to orange or amber it collapses
 * for red-green colour-blind readers. The legend and tooltip name each gas, so
 * identity never rests on colour alone.
 */
const GAS_ORDER = ["CO", "VOC", "H2S", "LEL"];
const SERIES_COLOURS: Record<"light" | "dark", Record<string, string>> = {
  light: { CO: "#2a78d6", VOC: "#eb6834", H2S: "#159e6f", LEL: "#b07800" },
  dark: { CO: "#3987e5", VOC: "#d95926", H2S: "#199e70", LEL: "#c98500" },
};
const OTHER_GAS = { light: "#6b7280", dark: "#9ca3af" };

/** Tracks the time-of-day theme class that lib/theme.ts toggles on <html>. */
function useIsDark(): boolean {
  const read = () =>
    typeof document !== "undefined" &&
    document.documentElement.classList.contains("dark");
  const [dark, setDark] = useState(read);
  useEffect(() => {
    const observer = new MutationObserver(() => setDark(read()));
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });
    return () => observer.disconnect();
  }, []);
  return dark;
}

type Row = { ts: string } & Record<string, number | string | null | undefined>;

export function ExposureChart({
  readings,
  tz,
}: {
  readings: GasReading[];
  tz?: string;
}) {
  const theme = useIsDark() ? "dark" : "light";
  if (!readings.length) {
    return (
      <div className="flex h-72 items-center justify-center rounded-md border border-dashed text-sm text-muted-foreground">
        No gas readings recorded for this incident.
      </div>
    );
  }
  // Pivot to one row per timestamp, one column per gas.
  const byTs = new Map<string, Row>();
  const gases = new Set<string>();
  for (const r of readings) {
    gases.add(r.gas);
    const existing = byTs.get(r.measured_at) ?? { ts: r.measured_at };
    existing[r.gas] = r.value_ppm;
    byTs.set(r.measured_at, existing);
  }
  const data = Array.from(byTs.values()).sort((a, b) =>
    String(a.ts).localeCompare(String(b.ts)),
  );
  const gasList = Array.from(gases).sort((a, b) => {
    const ia = GAS_ORDER.indexOf(a);
    const ib = GAS_ORDER.indexOf(b);
    return (ia < 0 ? GAS_ORDER.length : ia) - (ib < 0 ? GAS_ORDER.length : ib);
  });
  return (
    <div className="h-72 w-full">
      <ResponsiveContainer>
        <LineChart
          data={data}
          margin={{ top: 12, right: 24, bottom: 12, left: 0 }}
        >
          {/* Recessive grid, themed via the border token. Set as a class rather
              than the stroke prop: Recharts emits stroke as an SVG presentation
              attribute (where var() does not resolve), and a CSS declaration
              outranks a presentation attribute, so this follows light/dark. */}
          <CartesianGrid className="stroke-border" strokeDasharray="3 3" />
          <XAxis
            dataKey="ts"
            tick={{ fontSize: 11 }}
            tickFormatter={(v) => {
              try {
                const d = new Date(String(v));
                return tz
                  ? d.toLocaleTimeString("en-CA", {
                      timeZone: tz,
                      hour12: false,
                    })
                  : d.toLocaleTimeString("en-CA", { hour12: false });
              } catch {
                return String(v);
              }
            }}
          />
          <YAxis
            tick={{ fontSize: 11 }}
            label={{
              value: "ppm",
              angle: -90,
              position: "insideLeft",
              fontSize: 11,
            }}
          />
          <Tooltip />
          <Legend />
          {gasList.map((gas) => (
            <Line
              key={gas}
              type="monotone"
              dataKey={gas}
              stroke={SERIES_COLOURS[theme][gas] ?? OTHER_GAS[theme]}
              strokeDasharray={gas in SERIES_COLOURS[theme] ? undefined : "6 4"}
              dot={false}
              strokeWidth={2}
              isAnimationActive={false}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
